/**
 * Retention curve fitting utilities
 *
 * Fits exponential retention curves to cohort data and extracts
 * key retention metrics (D1, D7, D30).
 */

export interface RetentionDataPoint {
  day: number
  retention: number // cohort_active_users / cohort_total_users[day=0]
}

export interface RetentionCurveResult {
  d1: number    // Day 1 retention percentage (0-100)
  d7: number    // Day 7 retention percentage (0-100)
  d30: number   // Day 30 retention percentage (0-100)
}

/**
 * Fits an exponential curve to retention data using least squares regression.
 *
 * Model: retention(t) = a * exp(-k * t)
 *
 * Uses logarithmic transformation to convert to linear regression:
 * ln(retention) = ln(a) - k*t
 *
 * @param points Array of retention data points
 * @returns Curve parameters {a: initial retention, k: decay rate}
 */
function _leastSquaresExponential(points: RetentionDataPoint[]): { a: number; k: number } {
  if (points.length === 0) {
    return { a: 1, k: 0.1 } // Default fallback
  }

  const n = points.length
  let sumX = 0
  let sumY = 0
  let sumXY = 0
  let sumX2 = 0

  for (const point of points) {
    const x = point.day
    const y = Math.log(point.retention) // natural log

    sumX += x
    sumY += y
    sumXY += x * y
    sumX2 += x * x
  }

  // Linear regression formulas
  const denominator = n * sumX2 - sumX * sumX

  // Handle edge case where all points have same x value
  if (Math.abs(denominator) < 1e-10) {
    return { a: 1, k: 0.1 }
  }

  const slope = (n * sumXY - sumX * sumY) / denominator
  const intercept = (sumY - slope * sumX) / n

  const k = -slope  // decay rate (should be positive for decay)
  const a = Math.exp(intercept)  // initial retention (should be ~1.0 at t=0)

  return { a, k }
}

/**
 * Extracts D1, D7, D30 retention values from cohort data.
 *
 * Reads actual retention values directly from the data for days 1, 7, and 30.
 * Falls back to default values when specific days are not available in the data.
 *
 * Note: Curve fitting for Time Horizon projections is handled separately
 * in the edge.tsx calculateRetentionCurveParams function.
 *
 * @param dataPoints Array of retention data points from cohort analysis
 * @returns Retention percentages for days 1, 7, and 30
 */
export function fitRetentionCurve(dataPoints: RetentionDataPoint[]): RetentionCurveResult {
  // If no valid data, return all defaults
  if (dataPoints.length === 0) {
    return { d1: 40, d7: 20, d30: 10 }
  }

  // Simply extract actual retention values from the data for each day
  const day1Data = dataPoints.find(p => p.day === 1)
  const day7Data = dataPoints.find(p => p.day === 7)
  const day30Data = dataPoints.find(p => p.day === 30)

  // Use actual values when available, defaults otherwise
  const d1 = day1Data
    ? Math.min(100, Math.max(0, day1Data.retention * 100))
    : 40 // default

  const d7 = day7Data
    ? Math.min(100, Math.max(0, day7Data.retention * 100))
    : 20 // default

  const d30 = day30Data
    ? Math.min(100, Math.max(0, day30Data.retention * 100))
    : 10 // default

  return { d1, d7, d30 }
}

/**
 * Processes raw API retention data into RetentionDataPoint format.
 *
 * Calculates retention as: cohort_active_users[day_N] / cohort_total_users[day_0]
 *
 * @param apiResponse Raw API response with retention_day, cohort_active_users, cohort_total_users
 * @returns Processed and sorted retention data points
 */
export function processRetentionData(apiResponse: any[]): RetentionDataPoint[] {
  if (!apiResponse || apiResponse.length === 0) {
    return []
  }

  // Find day 0 cohort size (denominator for all retention calculations)
  const day0Data = apiResponse.find(d => d.retention_day === '0000' || d.retention_day === 0)
  const baseCohortSize = day0Data?.cohort_total_users || 0

  if (baseCohortSize === 0) {
    console.warn('No day 0 cohort data found, cannot calculate retention')
    return []
  }

  const result = apiResponse
    .filter(d => {
      // Filter out rows without valid retention_day or cohort_active_users
      return d.retention_day != null &&
             d.cohort_active_users != null &&
             !isNaN(parseInt(String(d.retention_day)))
    })
    .map(d => ({
      day: parseInt(String(d.retention_day)),
      retention: d.cohort_active_users / baseCohortSize
    }))
    .sort((a, b) => a.day - b.day)

  return result
}

// Suppress unused function warning - kept for potential future curve fitting
void _leastSquaresExponential
